import React, { useReducer, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Box, TextField, Button, Grid, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

import { createDatabaseConfig, updateDatabaseConfig, getDatabaseConfig } from '../../redux-store/currentUserActions';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import coreUtils from '../../utils/coreUtils';

const DBConfigForm = ({ project }) => {
    const dispatch = useDispatch();
    const [databaseConfig, setDatabaseConfig] = useState(null);
    const [state, setState] = useReducer(reducer, {
        id: '',
        db_type: 'postgresql',
        name: '',
        host: '',
        username: '',
        password: '',
        port: '',
        errors: {},
    });
    const { openSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const initialMount = useRef(true);

    useEffect(() => {
        if (initialMount.current) {
            initialMount.current = false;
            fetchDatabaseConfig();
        }
    }, []);

    useEffect(() => {
        if (databaseConfig) {
            setState({
                type: "SET_STATE",
                payload: {
                    id: databaseConfig?.id || '',
                    db_type: databaseConfig?.db_type || 'postgresql',
                    name: databaseConfig?.name || '',
                    host: databaseConfig?.host || '',
                    username: databaseConfig?.username || '',
                    password: databaseConfig?.password || '',
                    port: databaseConfig?.port || '',
                }
            });
        }
    }, [databaseConfig]);

    const fetchDatabaseConfig = async () => {
        try {
            const response = await dispatch(getDatabaseConfig({ projectId: project?.id }));
            if (response?.payload) {
                setDatabaseConfig(response.payload.database_configs[0]);
            }
        } catch (error) {
            openSnackbar({ message: "Failed to fetch database configuration", severity: 'error' });
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState({
            type: "SET_STATE",
            payload: { [name]: value },
        });
    };

    const validate = () => {
        let errors = {}, error = false;
        if (coreUtils.isStringInvalidOrBlank(state.name)) { errors["name"] = "Please enter database name"; error = true; }
        if (coreUtils.isStringInvalidOrBlank(state.host)) { errors["host"] = "Please enter host"; error = true; }
        setState({
            type: "SET_STATE",
            payload: {
                errors: errors,
            },
        });

        return error;
    }

    const onFormSubmit = async (data) => {
        let error = validate();
        if (error) return;
        setIsLoading(true);

        let databaseConfigForm = {
            dbType: state.db_type,
            name: state.name.trim(),
            host: state.host.trim(),
            username: state.username?.trim() || null,
            password: state.password?.trim() || null,
            port: state.port || null,
        }

        try {
            let response;
            if (!databaseConfig) {
                // Create new database config
                response = await dispatch(createDatabaseConfig({
                    projectId: project?.id,
                    ...databaseConfigForm
                }));
            } else {
                // Update existing database config
                response = await dispatch(updateDatabaseConfig({
                    databaseConfigId: databaseConfig.id,
                    ...databaseConfigForm
                }));
            }

            if (response?.payload?.id) {
                openSnackbar({
                    message: databaseConfig
                        ? "Database configuration updated successfully!"
                        : "Database configuration created successfully!",
                    severity: 'success'
                });
                setDatabaseConfig(response.payload);
            } else {
                openSnackbar({
                    message: databaseConfig
                        ? "Failed to update database configuration"
                        : "Failed to create database configuration",
                    severity: 'error'
                });
            }
        } catch (error) {
            openSnackbar({
                message: databaseConfig
                    ? "Database configuration update failed!"
                    : "Database configuration creation failed!",
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ my: 4 }}>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                    <Typography>Database Type</Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            name="db_type"
                            value={state.db_type}
                            onChange={handleInputChange}
                            variant="outlined"
                        >
                            <MenuItem value="postgresql">PostgreSQL</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <Typography>Database Name</Typography>
                    <TextField
                        fullWidth
                        size='small'
                        variant="outlined"
                        name="name"
                        value={state.name}
                        onChange={handleInputChange}
                        error={!!state.errors.name}
                        helperText={state.errors.name || ''}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography>Host</Typography>
                    <TextField
                        fullWidth
                        size='small'
                        variant="outlined"
                        name="host"
                        value={state.host}
                        onChange={handleInputChange}
                        error={!!state.errors.host}
                        helperText={state.errors.host || ''}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography>Port</Typography>
                    <TextField
                        fullWidth
                        size='small'
                        variant="outlined"
                        name="port"
                        value={state.port}
                        onChange={handleInputChange}
                        // error={!!state.errors.port}
                        // helperText={state.errors.port || 'Optional'}
                        placeholder=""
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography>Username</Typography>
                    <TextField
                        fullWidth
                        size='small'
                        variant="outlined"
                        name="username"
                        value={state.username}
                        onChange={handleInputChange}
                        // error={!!state.errors.username}
                        // helperText={state.errors.username || 'Optional'}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Typography>Password</Typography>
                    <TextField
                        fullWidth
                        size='small'
                        variant="outlined"
                        name="password"
                        type="text"
                        value={state.password}
                        onChange={handleInputChange}
                        // error={!!state.errors.password}
                        // helperText={state.errors.password || 'Optional'}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onFormSubmit}
                        disabled={isLoading}
                    >
                        Save Configuration
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DBConfigForm;

const reducer = (state, action) => {
    switch (action.type) {
        case "SET_STATE": {
            return {
                ...state,
                ...action.payload,
            };
        }
        default: {
            return state;
        }
    }
};
